// The Catholic University of America: public college page (#2840), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Catholic University posts no public Common Data Set: its Institutional
//    Research page says "To view the data sets, please visit the
//    Institutional Research Library (University login required)." The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 131283:
//    6,714 applied, 5,558 admitted, 742 enrolled; Navigator rounds the rate
//    to 83%).
//  - Catholic University Undergraduate Admission, First-Year Students,
//    Application Process (deadlines, the critical dates list, the Early
//    Decision terms, the test-optional policy and the recommended courses).
//    Quirk: the page names no entering year; its dates are the ones posted
//    for the cycle open on the read date. Its Regular Decision paragraph
//    says decisions come "in the first week of March", while its dates list
//    says "Mar. 20 Notification for Regular Decision applicants"; both are
//    stated in the deadlines note. No Early Decision II notification date is
//    published.
//  - The Rome School of Performing Arts admission page (audition rule). Its
//    dates are still for fall 2026 entry, so only the standing rule is used.
//  - Catholic University Tuition and Fees: the 2026-2027 undergraduate
//    estimated cost of attendance for a dependent resident living on or off
//    campus. Its items sum exactly to its $89,486 total.
//  - College Scorecard, school id 131283 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=131283#admsns',
        read: '2026-10-01',
    },
    apply: {
        label: 'Catholic University Undergraduate Admission, First-Year Application Process',
        url: 'https://www.catholic.edu/admission-aid/undergraduate-admission/first-year-students/application-process',
        read: '2026-10-01',
    },
    arts: {
        label: 'Catholic University, Rome School of Performing Arts, Admission',
        url: 'https://arts.catholic.edu/admission/index.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Catholic University, Tuition and Fees',
        url: 'https://www.catholic.edu/admission-aid/tuition-fees',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?131283-The-Catholic-University-of-America',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'the-catholic-university-of-america',
    kbId: 'the_catholic_university_of_america',
    name: 'The Catholic University of America',
    shortName: 'Catholic University',
    aliases: ['CUA', 'Catholic U'],
    location: 'Washington, DC',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Catholic University communications); decoration only.
    branding: {
        primary: '#990000',
        secondary: '#005E8C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/the_catholic_university_of_america_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 6714,
        admitted: 5558,
        enrolled: 742,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Catholic University publishes no first-year acceptance rate by school or major, and its Common Data Set is behind a university login, so the federal IPEDS count above is the one public first-year figure.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Catholic University offers Early Action and two binding Early Decision rounds. With no public Common Data Set, it publishes no count of early applicants or admits, so there is no early acceptance rate.',
        source: 'apply',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every first-year applicant sends the Common Application. Two areas ask for more:',
        rows: [
            { school: 'Rome School of Performing Arts', requirement: 'Admission needs both acceptance to the university and, for most programs, a successful audition. You submit audition materials through the Acceptd portal, where each program lists its own requirements; the two steps can be done in either order.', source: 'arts' },
            { school: 'Engineering, Physics, and Computing; Nursing; physical sciences', requirement: 'These recommend extra science and math in high school: math through pre-calculus and at least three laboratory sciences.', source: 'apply' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 1', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By January 1', binding: true },
            { plan: 'Early Decision II', deadline: 'February 1', decision: null, binding: true },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'March 20', binding: false },
        ],
        note: 'The Common Application opens August 1, and Catholic University charges no application fee. Its dates list gives March 20 for Regular Decision notification, while its Regular Decision description says the first week of March. It does not publish an Early Decision II notification date. Early Action and Regular Decision admits reply by May 1.',
        source: 'apply',
        binding: 'Early Decision I and II are binding. You, a parent or guardian and your school counselor sign an Early Decision Agreement; if admitted, you withdraw every other application and pay the non-refundable enrollment deposit by January 15 for Early Decision I or March 16 for Early Decision II. Early Action is not binding.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Catholic University does not require the SAT or ACT for admission, the University Honors Program or merit scholarships. If you send scores, they are considered as one part of a holistic review; if you do not, you are at no disadvantage. The policy covers home-schooled and international applicants too. It still requires first-year students to submit SAT or ACT scores after they enroll, as part of its academic assessments.',
        source: 'apply',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 60500 },
            { item: 'Fees', amount: 1774 },
            { item: 'Housing', amount: 12160 },
            { item: 'Food', amount: 8780 },
            { item: 'Books, supplies and licensure', amount: 1286 },
            { item: 'Transportation', amount: 1068 },
            { item: 'Personal expenses', amount: 2634 },
            { item: 'Estimated loan fees', amount: 1284 },
        ],
        total: 89486,
        note: 'Catholic University\'s estimate for a dependent undergraduate living on or off campus. Tuition for Architecture and Engineering students is $61,240.',
        source: 'costs',
        netPrice: {
            average: 32543,
            byIncome: [
                { income: '$0 to $30,000', amount: 27245 },
                { income: '$30,001 to $48,000', amount: 24802 },
                { income: '$48,001 to $75,000', amount: 26171 },
                { income: '$75,001 to $110,000', amount: 28183 },
                { income: 'Over $110,000', amount: 37786 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Catholic University\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
